import React from 'react';

/**
 * Removes elements with a specific class from React children tree
 * @param children - React children to process
 * @param classToRemove - Class name to identify elements to remove
 * @returns Processed React children with specified elements removed
 */
export function removeElementsByClass(
  children: React.ReactNode,
  classToRemove: string
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    // Check if this element has the specified class
    const childProps = child.props as any;
    const className = childProps?.className || '';
    const hasClass = className.includes(classToRemove);

    // If it has the class, remove this element
    if (hasClass) {
      return null;
    }

    // Recursively process children
    const processedChildren = childProps?.children
      ? removeElementsByClass(childProps.children, classToRemove)
      : childProps?.children;

    // Clone element with processed children if children changed
    if (processedChildren !== childProps?.children) {
      return React.cloneElement(child, {
        ...childProps,
        children: processedChildren,
      } as any);
    }

    return child;
  });
}

/**
 * Conditionally adds or removes a class from elements based on a condition
 * @param children - React children to process
 * @param targetClass - Class name to identify target elements
 * @param classToToggle - Class name to add or remove
 * @param shouldAdd - If true, adds the class; if false, removes it
 * @returns Processed React children with class toggled
 */
export function toggleClassOnElements(
  children: React.ReactNode,
  targetClass: string,
  classToToggle: string,
  shouldAdd: boolean
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    // Check if this element has the target class
    const childProps = child.props as any;
    const className = childProps?.className || '';
    const hasTargetClass = className.includes(targetClass);

    let newClassName = className;
    if (hasTargetClass) {
      // Add or remove the toggle class based on condition
      if (shouldAdd) {
        // Add class if not already present
        if (!className.includes(classToToggle)) {
          newClassName = className + ' ' + classToToggle;
        }
      } else {
        // Remove class if present
        newClassName = className.replace(new RegExp(`\\s*${classToToggle}\\s*`, 'g'), ' ').trim();
      }
    }

    // Recursively process children
    const processedChildren = childProps?.children
      ? toggleClassOnElements(childProps.children, targetClass, classToToggle, shouldAdd)
      : childProps?.children;

    // Clone element with updated className if changed
    if (newClassName !== className || processedChildren !== childProps?.children) {
      return React.cloneElement(child, {
        ...childProps,
        className: newClassName,
        children: processedChildren,
      } as any);
    }

    return child;
  });
}

/**
 * Renumbers text content in React children based on animal type
 * For fish and other non-dog/cat animals, numbering starts from 1
 * This function is intentionally removed - renumbering should be done in JSON config instead
 * @param children - React children to process
 * @param animalType - Type of animal (dog, cat, fish, etc.)
 * @returns Original children unchanged
 */
export function renumberFieldsByAnimalType(
  children: React.ReactNode,
  animalType: string
): React.ReactNode {
  // This approach doesn't work well with React's reconciliation
  // The numbering should be corrected in the JSON template configuration instead
  // Simply return children as-is
  return children;
}
